import { Outlet, useNavigate } from "react-router-dom";

import GridLoader from "@/ui/molecules/GridLoader";
import ErrorBoundary from "@/ui/organisms/ErrorBoundary";

import LayoutFullScreen from "@/ui/layouts/LayoutFullScreen";

import {
    ViewDatabaseGuard,
    ViewDealList,
    ViewSelectDatabaseModal,
    ViewExportModal,
    ViewTopBar,
    ViewNewExperienceSection,
    ViewNavigationSidebar,
} from "@/ui/compositionRootUI";

import { routerPaths } from "@/ui/router/routerPaths";
import { VIEW_DEAL_LIST_ACTION_BAR_HEIGHT } from "@/ui/views/ViewDealList";
import { createProdaLinks } from "@/utils/createProdaLink";

const PageDealTable = () => {
    const navigate = useNavigate();

    const handleCreateDeal = (databaseId: number) => {
        navigate(
            routerPaths.projects.create({ databaseId: databaseId.toString() }),
        );
    };

    const handleDealOverview = ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        navigate(
            routerPaths.projects.read({
                databaseId: databaseId.toString(),
                projectId: projectId.toString(),
                tab: "documents",
            }),
        );
    };

    const handleAddFileButtonClicked = ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        navigate(
            routerPaths.files.upload.url({
                projectId,
                databaseId,
            }),
        );
    };

    const getDealUrl = ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        return routerPaths.projects.read({
            databaseId: databaseId.toString(),
            projectId: projectId.toString(),
            tab: "documents",
        });
    };
    const getExtractionUrl = ({
        projectId,
        databaseId,
        extractionId,
        extractionStatus,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => {
        return extractionStatus === "completed"
            ? routerPaths.extraction.review({
                  projectId: String(projectId),
                  databaseId: String(databaseId),
                  extractionId: String(extractionId),
              })
            : routerPaths.extraction.mappings({
                  projectId: String(projectId),
                  databaseId: String(databaseId),
                  extractionId: String(extractionId),
              });
    };

    const getRentRollExtractionUrl = ({
        fileId,
        extractionId,
        sheetId,
        databaseId,
    }: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }): string => {
        return createProdaLinks(
            `/rr/upload/workbooks/${fileId}/worksheets/${sheetId}/rentrolls/${extractionId}/edit`,
            [databaseId],
        );
    };

    const handleViewExtractionButtonClick = (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => {
        navigate(getExtractionUrl(params));
    };

    return (
        <LayoutFullScreen
            TopBarProps={{
                sx: {
                    backgroundColor: "var(--proda-white-110)",
                },
                onLogoClick: () => navigate(routerPaths.main.default()),
            }}
            MainContentProps={{
                sx: {
                    backgroundColor: "var(--proda-white)",
                    borderTop: "1px solid var(--primary-color)",
                },
            }}
            contentSidebar={<ViewNavigationSidebar />}
            contentTopBar={<ViewTopBar />}
            contentTopBarBanner={<ViewNewExperienceSection />}
        >
            <ErrorBoundary>
                <ViewDatabaseGuard
                    loader={
                        <GridLoader
                            sx={{
                                paddingTop: `${VIEW_DEAL_LIST_ACTION_BAR_HEIGHT}px`,
                            }}
                        />
                    }
                >
                    <ViewDealList
                        getDealUrl={getDealUrl}
                        getRentRollExtractionUrl={getRentRollExtractionUrl}
                        getExtractionUrl={getExtractionUrl}
                        handleCreateDealButtonClicked={handleCreateDeal}
                        handleDealViewButtonClicked={handleDealOverview}
                        handleAddFileButtonClicked={handleAddFileButtonClicked}
                        handleViewExtractionButtonClick={
                            handleViewExtractionButtonClick
                        }
                        handleDraftResumeButtonClicked={({
                            projectId,
                            extractionId,
                            databaseId,
                        }) => {
                            handleViewExtractionButtonClick({
                                projectId,
                                databaseId,
                                extractionId,
                                extractionStatus: "draft",
                            });
                        }}
                        sx={{
                            height: "100%",
                        }}
                    />
                    <ViewSelectDatabaseModal />
                    <ViewExportModal />
                    <Outlet />
                </ViewDatabaseGuard>
            </ErrorBoundary>
        </LayoutFullScreen>
    );
};

export default PageDealTable;
